import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ShieldCheck, CheckCircle2, Lock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

/**
 * /claim-admin — one-time founder bootstrap.
 *
 * Lets the first user (founder) become an admin without needing the BLOG_API_KEY.
 * Once one admin exists in the database, the page permanently shows a "locked"
 * state and the backend rejects further claims with 403.
 */
export default function ClaimAdminPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth({ redirectIfUnauthed: false });
  const [available, setAvailable] = useState(null);
  const [adminCount, setAdminCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/admin/claim/available")
      .then((r) => {
        if (!mounted) return;
        setAvailable(!!r.data?.available);
        setAdminCount(r.data?.admin_count ?? null);
      })
      .catch(() => { if (mounted) setError("Couldn't check claim status. Try again later."); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const claim = async () => {
    setError("");
    setClaiming(true);
    try {
      await api.post("/admin/claim");
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Claim failed. Try refreshing the page.");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Claim admin · AgentReply</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="w-full max-w-lg bg-[#1A1715] border border-[#D95B38]/30 p-8 md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
          <ShieldCheck className="w-3 h-3" /> Founder bootstrap · one-time
        </div>

        {loading || authLoading ? (
          <div className="font-mono text-xs text-[#A39A8F] flex items-center gap-2 py-6">
            <Loader2 className="w-3 h-3 animate-spin" /> Checking claim status…
          </div>
        ) : done ? (
          // Success
          <>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-400" strokeWidth={1.8} />
              <h1 className="font-serif text-2xl leading-tight">You're admin.</h1>
            </div>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              <span className="text-[#E0D8CA]">{user?.email}</span> now has admin access. This page is permanently
              locked — no one else can claim. From now on: tap the AgentReply logo
              <span className="text-[#F5F2EB]"> 5 times fast</span> from any page → ops dashboard.
            </p>
            <button
              type="button"
              onClick={() => navigate("/dashboard/admin/health")}
              data-testid="claim-go-ops"
              className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] px-6 py-3 transition-colors btn-press flex items-center justify-center gap-2"
            >
              Open ops dashboard <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </>
        ) : !available ? (
          // Already claimed — locked
          <>
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-[#A39A8F]" strokeWidth={1.8} />
              <h1 className="font-serif text-2xl leading-tight">Door is locked.</h1>
            </div>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              An admin already exists ({adminCount ?? "?"} total). This one-time claim is permanently
              closed. If you need admin access, ask an existing admin to promote you.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="claim-back-home"
              className="w-full font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/30 text-[#F5F2EB] hover:bg-[#F5F2EB]/5 px-6 py-3 transition-colors"
            >
              Back to home
            </button>
          </>
        ) : !user ? (
          // Available but not signed in
          <>
            <h1 className="font-serif text-2xl md:text-3xl leading-tight mb-3">Sign in first.</h1>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              The first admin claim is still open. To claim it, you need to be signed in first —
              the system promotes <span className="text-[#E0D8CA]">your account</span>, not an
              arbitrary email.
            </p>
            <button
              type="button"
              onClick={() => navigate("/login?next=/claim-admin")}
              data-testid="claim-sign-in"
              className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] px-6 py-3 transition-colors btn-press"
            >
              Sign in →
            </button>
          </>
        ) : (
          // Available + signed in → big claim button
          <>
            <h1 className="font-serif text-2xl md:text-3xl leading-tight mb-3">
              Claim founder admin.
            </h1>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              No admin exists yet on this environment. Click below and your account
              (<span className="text-[#E0D8CA]">{user.email}</span>) becomes admin instantly.
              After this, the door locks forever — no one else can use this page.
            </p>

            {error && (
              <div className="font-mono text-xs text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2 mb-5" data-testid="claim-error">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={claim}
              disabled={claiming}
              data-testid="claim-submit"
              className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] disabled:opacity-60 px-6 py-3 transition-colors btn-press flex items-center justify-center gap-2"
            >
              {claiming && <Loader2 className="w-4 h-4 animate-spin" />}
              {claiming ? "Claiming…" : "Claim admin (one-time) →"}
            </button>

            <p className="font-mono text-[10px] text-[#7A7068] mt-6 leading-relaxed">
              This is irreversible. The first user who clicks "Claim" becomes the permanent founder admin.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
